import React from 'react';
import { Checkbox, Divider, FieldGroup, Row, Stack, Tabs, Text, TextInput, TimePicker, ToggleGroup } from 'library/ui';
import { UpperDaysOfWeek, daysOfTheWeek } from 'shared/Dates';
import { NumberStepper } from './NumberStepper';
import type { ShiftTimes, TimesMode } from '../formTypes';

const MONDAY_FIRST_DAYS = [...daysOfTheWeek.slice(1), daysOfTheWeek[0]];
const DAY_OPTIONS = MONDAY_FIRST_DAYS.map((day) => ({ value: day, label: day.slice(0, 3) }));

const TIMES_MODE_OPTIONS: { label: string; value: TimesMode }[] = [
    { label: 'Same time every day', value: 'same' },
    { label: 'Different per day', value: 'perDay' },
];

export interface DaysTimesFieldsProps {
    availableDays: UpperDaysOfWeek[];
    selectedDays: UpperDaysOfWeek[];
    onSelectedDaysChange: (days: UpperDaysOfWeek[]) => void;
    timesMode: TimesMode;
    onTimesModeChange: (mode: TimesMode) => void;
    times: ShiftTimes;
    onTimesChange: (times: ShiftTimes) => void;
    perDayTimes: Record<UpperDaysOfWeek, ShiftTimes>;
    onDayTimesChange: (day: UpperDaysOfWeek, times: ShiftTimes) => void;
    includeBreak?: boolean;
    onIncludeBreakChange?: (value: boolean) => void;
    breakLength?: string;
    onBreakLengthChange?: (value: string) => void;
    showBreak?: boolean;
    showSlots?: boolean;
}

export function DaysTimesFields({
    availableDays,
    selectedDays,
    onSelectedDaysChange,
    timesMode,
    onTimesModeChange,
    times,
    onTimesChange,
    perDayTimes,
    onDayTimesChange,
    includeBreak = false,
    onIncludeBreakChange,
    breakLength = '',
    onBreakLengthChange,
    showBreak = true,
    showSlots = true,
}: DaysTimesFieldsProps) {
    return (
        <>
            <FieldGroup label="Which Days?" required>
                <ToggleGroup
                    multi
                    variant="button"
                    options={DAY_OPTIONS}
                    value={selectedDays}
                    onChange={(days) => onSelectedDaysChange(MONDAY_FIRST_DAYS.filter((day) => days.includes(day)))}
                    disabledOptions={daysOfTheWeek.filter((day) => !availableDays.includes(day))}
                />
            </FieldGroup>
            <FieldGroup label="Times & Coverage" required>
                <Row>
                    <Tabs
                        tabs={TIMES_MODE_OPTIONS.map((option) => ({ key: option.value, label: option.label }))}
                        activeTab={timesMode}
                        onTabChange={(key) => onTimesModeChange(key as TimesMode)}
                    />
                </Row>
            </FieldGroup>
            {timesMode === 'same' ? (
                <Row gap="lg" align="end" wrap>
                    <FieldGroup label="Start Time" required>
                        <TimePicker value={times.start} onChange={(start) => onTimesChange({ ...times, start })} />
                    </FieldGroup>
                    <FieldGroup label="End Time" required>
                        <TimePicker value={times.end} onChange={(end) => onTimesChange({ ...times, end })} />
                    </FieldGroup>
                    {showSlots && (
                        <FieldGroup label="Subs Per Shift">
                            <NumberStepper
                                value={times.slots}
                                onChange={(slots) => onTimesChange({ ...times, slots })}
                                unit="sub"
                            />
                        </FieldGroup>
                    )}
                </Row>
            ) : (
                <Stack gap="md">
                    {selectedDays.length === 0 ? (
                        <Text tone="secondary">Select at least one day above to set its times.</Text>
                    ) : (
                        selectedDays.map((day) => {
                            const dayTimes = perDayTimes[day];
                            return (
                                <FieldGroup key={day} label={day}>
                                    <Row gap="sm" wrap>
                                        <TimePicker
                                            value={dayTimes.start}
                                            onChange={(start) => onDayTimesChange(day, { ...dayTimes, start })}
                                        />
                                        <Text tone="secondary">to</Text>
                                        <TimePicker
                                            value={dayTimes.end}
                                            onChange={(end) => onDayTimesChange(day, { ...dayTimes, end })}
                                        />
                                        {showSlots && (
                                            <NumberStepper
                                                value={dayTimes.slots}
                                                onChange={(slots) => onDayTimesChange(day, { ...dayTimes, slots })}
                                                unit="sub"
                                            />
                                        )}
                                    </Row>
                                </FieldGroup>
                            );
                        })
                    )}
                </Stack>
            )}
            {showBreak && (
                <>
                    <Divider />
                    <FieldGroup label="Breaks">
                        <Row gap="sm">
                            <Checkbox
                                checked={includeBreak}
                                onChange={(value) => onIncludeBreakChange?.(value)}
                                label="This shift includes a break"
                            />
                            {includeBreak && (
                                <>
                                    <TextInput value={breakLength} onChange={(value) => onBreakLengthChange?.(value)} />
                                    <Text tone="secondary">minutes</Text>
                                </>
                            )}
                        </Row>
                    </FieldGroup>
                </>
            )}
        </>
    );
}
